"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import {
  Button,
  Divider,
  InputAdornment,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import SearchIcon from "@mui/icons-material/Search";
import {
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
  Toolbar,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import {
  GridToolbarProps,
  useGridSelector,
  gridRowSelectionIdsSelector,
} from "@mui/x-data-grid-pro";
import { gridRowSelectionCountSelector } from "@mui/x-data-grid/hooks/features/rowSelection";
import { useAssignDialog } from "../Providers/AssignDialogContext";
import { useCampaignDialogs } from "../Providers/CampaignDialogsContext";
import { useCloneDialog } from "../Providers/CloneDialogContext";
import { useDeleteDialog } from "../Providers/DeleteDialogContext";
import { useEditDialog } from "../Providers/EditDialogContext";
import { useNotifyDialog } from "../Providers/NotifyDialogContext";
import { useUnassignDialog } from "../Providers/UnassignDialogContext";
import {
  StyledQuickFilter,
  StyledToolbarButton,
  StyledTextField,
  ExportButton,
} from "./MuiDefaultComponents";
import { FetchCampaignsV2Response } from "@/api/interfaces/responses/campaigns";

export function CustomToolbar(_props: GridToolbarProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isAdmin, currentUser } = useCampaignDialogs();
  const { openAssignDialog } = useAssignDialog();
  const { openUnassignDialog } = useUnassignDialog();
  const { openNotificationsDialog } = useNotifyDialog();
  const { openCloneDialog } = useCloneDialog();
  const { openDeleteDialog } = useDeleteDialog();
  const { openEditDialog } = useEditDialog();

  const apiRef = useGridApiContext();
  const selectedCount = useGridSelector(apiRef, gridRowSelectionCountSelector);
  const rows = useGridSelector(apiRef, gridRowSelectionIdsSelector);
  const rowIds = Array.from(rows.keys());

  const canEdit =
    selectedCount === 1 &&
    (isAdmin ||
      (currentUser &&
        Array.from(rows.values())[0]?.creator?.id === currentUser.id));

  const filterValue = searchParams?.get("filter") || "all";
  const searchValue = searchParams?.get("search") || "";
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Sync local state with URL params
  useEffect(() => {
    setLocalSearchValue(searchValue);
  }, [searchValue]);

  useEffect(() => {
    if (searchValue !== localSearchValue) {
      debounceTimeoutRef.current = setTimeout(() => {
        const params = new URLSearchParams(searchParams);
        if (localSearchValue) {
          params.set("search", localSearchValue);
        } else {
          params.delete("search");
        }
        // Reset to first page when search changes
        params.set("page", "1");
        // Delete per-page param if it's less than 10
        const perPage = params.get("per-page");
        if (perPage && parseInt(perPage) < 10) {
          params.delete("per-page");
        }
        router.push(`/campaigns?${params}`);
      }, 300);
    }
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [localSearchValue, router, searchParams, searchValue]);

  const onFilterChange = useCallback(
    (value: string) => {
      const params = new URLSearchParams(searchParams?.toString() || "");
      if (value === "all") {
        params.delete("filter");
      } else {
        params.set("filter", value);
        // Reset to first page when filter changes
        params.set("page", "1");
      }
      router.push(`/campaigns?${params.toString()}`);
    },
    [router, searchParams],
  );

  const hasSelection = selectedCount > 0;

  const handleOpenCloneDialog = () => {
    openCloneDialog(rowIds);
  };

  const handleOpenDeleteDialog = () => {
    openDeleteDialog(rowIds);
  };

  const handleOpenNotificationsDialog = () => {
    openNotificationsDialog(rowIds);
  };

  const handleOpenAssignDialog = () => {
    openAssignDialog(rowIds);
  };

  const handleOpenUnassignDialog = () => {
    openUnassignDialog(rowIds);
  };

  const handleOpenEditDialog = () => {
    if (selectedCount === 1 && rowIds.length > 0) {
      const campaignId = rowIds[0];
      // Get the selected campaign from the grid row data
      const rowModels = apiRef.current?.getRowModels();
      const selectedCampaign = rowModels?.get(campaignId) as
        | FetchCampaignsV2Response
        | undefined;
      if (selectedCampaign) {
        openEditDialog(selectedCampaign);
      }
    }
  };

  return (
    <Toolbar>
      <div className="flex gap-2 grow">
        <Button
          onClick={handleOpenCloneDialog}
          disabled={!hasSelection}
          startIcon={<ContentCopyIcon />}
          variant="outlined"
          className="text-transform-capitalize whitespace-nowrap"
        >
          Clone
        </Button>
        <Button
          onClick={handleOpenEditDialog}
          disabled={!canEdit}
          startIcon={<EditIcon />}
          variant="outlined"
          className="text-transform-capitalize whitespace-nowrap"
        >
          Edit
        </Button>
        {isAdmin && (
          <Button
            onClick={handleOpenDeleteDialog}
            disabled={!hasSelection}
            startIcon={<DeleteIcon />}
            variant="outlined"
            className="text-transform-capitalize whitespace-nowrap"
            color="error"
          >
            Delete
          </Button>
        )}
        <Button
          onClick={handleOpenNotificationsDialog}
          disabled={!hasSelection}
          startIcon={<NotificationsIcon />}
          variant="outlined"
          className="text-transform-capitalize whitespace-nowrap"
        >
          Notifications
        </Button>
        {isAdmin && (
          <Button
            onClick={handleOpenAssignDialog}
            disabled={!hasSelection}
            startIcon={<PersonAddIcon />}
            variant="outlined"
            className="text-transform-capitalize whitespace-nowrap"
          >
            Assign
          </Button>
        )}
        {isAdmin && (
          <Button
            onClick={handleOpenUnassignDialog}
            disabled={!hasSelection}
            startIcon={<PersonRemoveIcon />}
            variant="outlined"
            className="text-transform-capitalize whitespace-nowrap"
          >
            Unassign
          </Button>
        )}
      </div>
      <Select
        size="small"
        value={filterValue}
        onChange={(e) => onFilterChange(e.target.value)}
      >
        <MenuItem value="all">All Campaigns</MenuItem>
        <MenuItem value="created-by-me">Created by Me</MenuItem>
        <MenuItem value="assigned-to-me">Assigned to Me</MenuItem>
      </Select>
      <Divider
        orientation="vertical"
        variant="middle"
        flexItem
        className="mx-1"
      />
      <ExportButton />
      <StyledQuickFilter>
        <QuickFilterTrigger
          render={(triggerProps, state) => (
            <Tooltip title="Search" enterDelay={0}>
              <StyledToolbarButton
                {...triggerProps}
                ownerState={{
                  expanded: Boolean(localSearchValue) || state.expanded,
                }}
                color="default"
                aria-disabled={Boolean(localSearchValue) || state.expanded}
              >
                <SearchIcon fontSize="small" />
              </StyledToolbarButton>
            </Tooltip>
          )}
        />
        <QuickFilterControl
          render={({ ref, ...controlProps }, state) => (
            <StyledTextField
              {...controlProps}
              ownerState={{
                expanded: Boolean(localSearchValue) || state.expanded,
              }}
              inputRef={ref}
              aria-label="Search"
              placeholder="Search..."
              size="small"
              value={localSearchValue}
              onChange={(e) => setLocalSearchValue(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: localSearchValue ? (
                    <InputAdornment position="end">
                      <QuickFilterClear
                        edge="end"
                        size="small"
                        aria-label="Clear search"
                        onClick={() => setLocalSearchValue("")}
                        material={{ className: "-mr-3" }}
                      >
                        <CancelIcon fontSize="small" />
                      </QuickFilterClear>
                    </InputAdornment>
                  ) : null,
                  ...controlProps.slotProps?.input,
                },
                ...controlProps.slotProps,
              }}
            />
          )}
        />
      </StyledQuickFilter>
    </Toolbar>
  );
}
